import { useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type StudentProfile = {
  student_id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  identity_no: string | null;
  license_class: string | null;
  exam_date: string | null;
  status: string;
  branch_id: string;
  days_to_exam: number | null;
  total_completed: number;
  total_completed_minutes: number;
  total_upcoming: number;
  next_starts_at: string | null;
  total_km: number;
};

export type StudentAgendaItem = {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  lesson_type: "normal" | "special" | "extra" | string;
  status: "planned" | "in_progress" | "completed" | "cancelled" | string;
  instructor_id: string;
  instructor_name: string;
  instructor_phone: string | null;
  vehicle_id: string;
  vehicle_plate: string;
  vehicle_brand_model: string | null;
  vehicle_gear_type: string | null;
  has_report: boolean;
  report_attended: boolean | null;
  report_driving_score: number | null;
  report_km_delta: number | null;
};

export type StudentAgendaSummary = {
  total: number;
  planned: number;
  completed: number;
  total_completed_minutes: number;
};

export type StudentAgendaResponse = {
  items: StudentAgendaItem[];
  summary: StudentAgendaSummary;
  filter: { student_id: string; start_date: string; end_date: string };
};

export function useStudentProfile() {
  const [data, setData] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<StudentProfile>("/students/me/profile", undefined, { signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Profil Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}

export function useStudentAgenda(startDate: string, endDate: string) {
  const [data, setData] = useState<StudentAgendaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<StudentAgendaResponse>(
        "/students/me/agenda",
        { start_date: startDate, end_date: endDate },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Ajanda Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [startDate, endDate, reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}
